import { useState } from 'react';
import { Box, Group, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { FeatureFlagResponse, FeatureFlagStatus } from './useAdminFeatureFlags';
import { DistributionBar } from './FlagMatrixShared';
import { StateCounts, resolvedOnCount, targetedCount } from './flagMatrixTypes';

type SortKey = 'name' | 'on' | 'targeted' | 'status';

type Props = {
  flags: FeatureFlagResponse[];
  countsFor: (flagKey: string) => StateCounts;
  tenantTotal: number;
  onSelectFlag: (flagKey: string) => void;
};

const STATUS_ORDER: Record<FeatureFlagStatus, number> = {
  fully_released: 0,
  partially_released: 1,
  disabled: 2,
};
const STATUS_DOT: Record<FeatureFlagStatus, string> = {
  fully_released: 'green.6',
  partially_released: 'yellow.5',
  disabled: 'gray.5',
};

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'on', label: 'Most tenants on' },
  { value: 'targeted', label: 'Most targeting' },
  { value: 'status', label: 'Status' },
];

export default function FlagCentricList({ flags, countsFor, tenantTotal, onSelectFlag }: Props) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('name');

  const query = search.trim().toLowerCase();
  const rows = flags
    .filter(
      flag =>
        !query || flag.key.toLowerCase().includes(query) || flag.name.toLowerCase().includes(query),
    )
    .map(flag => ({ flag, counts: countsFor(flag.key) }))
    .sort((a, b) => {
      if (sort === 'on') return resolvedOnCount(b.counts) - resolvedOnCount(a.counts);
      if (sort === 'targeted') return targetedCount(b.counts) - targetedCount(a.counts);
      if (sort === 'status') {
        return (
          STATUS_ORDER[a.flag.status] - STATUS_ORDER[b.flag.status] ||
          a.flag.key.localeCompare(b.flag.key)
        );
      }
      return a.flag.key.localeCompare(b.flag.key);
    });

  return (
    <Stack gap="sm">
      <Group>
        <TextInput
          placeholder="Search flags"
          value={search}
          onChange={event => setSearch(event.currentTarget.value)}
        />
        <Select
          data={SORT_OPTIONS}
          value={sort}
          onChange={value => setSort((value as SortKey) ?? 'name')}
          allowDeselect={false}
        />
      </Group>
      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Flag</Table.Th>
            <Table.Th w={220}>Distribution across tenants</Table.Th>
            <Table.Th w={100}>On</Table.Th>
            <Table.Th w={90}>Targeted</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map(({ flag, counts }) => (
            <Table.Tr
              key={flag.key}
              style={{ cursor: 'pointer' }}
              onClick={() => onSelectFlag(flag.key)}
            >
              <Table.Td>
                <Group gap={8} wrap="nowrap">
                  <Box
                    w={7}
                    h={7}
                    bg={STATUS_DOT[flag.status]}
                    style={{ borderRadius: 999, flex: 'none' }}
                  />
                  <Text ff="monospace" size="sm">
                    {flag.key}
                  </Text>
                </Group>
              </Table.Td>
              <Table.Td>
                <DistributionBar counts={counts} total={tenantTotal} />
              </Table.Td>
              <Table.Td>
                <Text size="sm">
                  {resolvedOnCount(counts)}{' '}
                  <Text span c="dimmed">
                    / {tenantTotal}
                  </Text>
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" c={targetedCount(counts) ? 'blue.7' : 'dimmed'}>
                  {targetedCount(counts)}
                </Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
